/**
 * Facade for a project's rule evaluation jobs.
 *
 * Every route here is project_admin only, reads included, matching the
 * backend's `require_project_admin()` on the whole surface.
 */

import { ApiFacade, ApiErrorPayload } from './apiFacade';
import { RuleEvaluationJob, RuleEvaluationResult } from '../../@types/jobs';
import { API_BASE_URL } from '../../apiConfig';

const PROJECTS_URL = `${API_BASE_URL}/api/v1/projects`;

/**
 * A queued or running job already exists for this project. The error's
 * `extra.object_id` is that job's id, so the caller can jump straight to it
 * instead of treating this as a plain failure.
 */
export const RULE_EVALUATION_ALREADY_ACTIVE = 'rule_evaluation_already_active';

export function isEvaluationAlreadyActive(error: ApiErrorPayload): boolean {
    return error.code === RULE_EVALUATION_ALREADY_ACTIVE;
}

export function activeEvaluationJobId(error: ApiErrorPayload): string | undefined {
    const objectId = error.extra?.object_id;
    return typeof objectId === 'string' ? objectId : undefined;
}

function jobsUrl(projectId: string): string {
    return `${PROJECTS_URL}/${encodeURIComponent(projectId)}/rule-evaluations`;
}

export async function triggerJob(accessToken: string, projectId: string): Promise<RuleEvaluationJob> {
    return ApiFacade.postJson<RuleEvaluationJob>(jobsUrl(projectId), { accessToken });
}

export async function getJobs(accessToken: string, projectId: string): Promise<RuleEvaluationJob[]> {
    const response = await ApiFacade.getJson<{ jobs: RuleEvaluationJob[] }>(jobsUrl(projectId), { accessToken });
    return response.jobs;
}

export async function getJob(accessToken: string, projectId: string, jobId: string): Promise<RuleEvaluationJob> {
    return ApiFacade.getJson<RuleEvaluationJob>(`${jobsUrl(projectId)}/${encodeURIComponent(jobId)}`, { accessToken });
}

export async function getJobResults(
    accessToken: string,
    projectId: string,
    jobId: string
): Promise<RuleEvaluationResult[]> {
    const response = await ApiFacade.getJson<{ results: RuleEvaluationResult[] }>(
        `${jobsUrl(projectId)}/${encodeURIComponent(jobId)}/results`,
        { accessToken }
    );
    return response.results;
}
